import React, { useEffect, useMemo, useState } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  CircularProgress,
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ContactsIcon from '@mui/icons-material/Contacts';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getLiveFolders, liveMoveMessage, Folder } from '../api/mailbox';
import { getMailAccounts } from '../api/auth';
import { isLiveTarget, readMessageDragTarget } from './MessageContextMenu';
import { liveFolderPath, decodeFolderKey } from '../utils/imapPaths';

const SPECIAL_ICONS: Record<string, React.ReactNode> = {
  inbox: <InboxIcon />,
  sent: <SendIcon />,
  drafts: <DraftsIcon />,
  trash: <DeleteIcon />,
  archive: <ArchiveIcon />,
  spam: <FolderIcon />,
  custom: <FolderIcon />,
};

const SPECIAL_ORDER: Record<string, number> = {
  inbox: 0,
  sent: 1,
  drafts: 2,
  archive: 3,
  trash: 4,
  spam: 5,
  custom: 9,
};

interface TreeNode extends Folder {
  children: TreeNode[];
}

function imapKey(accountId: number, imapName: string) {
  return `${accountId}::${imapName}`;
}

function inferDelimiter(imapName: string, allNames: string[]): string {
  for (const d of ['.', '/', '\\']) {
    if (imapName.includes(d) && allNames.some((n) => n !== imapName && imapName.startsWith(n + d))) {
      return d;
    }
  }
  if (imapName.includes('.')) return '.';
  if (imapName.includes('/')) return '/';
  return '.';
}

function buildTree(folders: Folder[]): TreeNode[] {
  const nodes = new Map<number, TreeNode>();
  folders.forEach((f) => nodes.set(f.id, { ...f, children: [] }));

  const byImap = new Map<string, TreeNode>();
  nodes.forEach((n) => byImap.set(imapKey(n.account, n.imap_name), n));

  const roots: TreeNode[] = [];
  nodes.forEach((node) => {
    const accountFolders = folders.filter((f) => f.account === node.account).map((f) => f.imap_name);
    const delim = node.delimiter || inferDelimiter(node.imap_name, accountFolders);
    let parentNode: TreeNode | undefined;

    const sep = node.imap_name.lastIndexOf(delim);
    if (sep > 0) {
      parentNode = byImap.get(imapKey(node.account, node.imap_name.slice(0, sep)));
    }
    if (!parentNode && node.parent != null) {
      parentNode = nodes.get(node.parent);
    }

    if (parentNode && parentNode.id !== node.id) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list: TreeNode[]) => {
    list.sort((a, b) => {
      const sa = SPECIAL_ORDER[a.special_use] ?? 9;
      const sb = SPECIAL_ORDER[b.special_use] ?? 9;
      if (sa !== sb) return sa - sb;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
    list.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

function collectExpandableIds(nodes: TreeNode[], into: Set<number>) {
  nodes.forEach((n) => {
    if (n.children.length > 0) {
      into.add(n.id);
      collectExpandableIds(n.children, into);
    }
  });
}

interface SidebarProps {
  onNavigate?: () => void;
}

function FolderItem({
  node,
  depth,
  selectedPath,
  onOpen,
  expanded,
  toggle,
  dropTargetId,
  setDropTargetId,
  onDropMessage,
}: {
  node: TreeNode;
  depth: number;
  selectedPath: string;
  onOpen: (path: string, opts?: { toggleIfParent?: boolean }) => void;
  expanded: Set<number>;
  toggle: (id: number) => void;
  dropTargetId: number | null;
  setDropTargetId: (id: number | null) => void;
  onDropMessage: (target: ReturnType<typeof readMessageDragTarget>, folder: Folder) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const folderPath = liveFolderPath(node.account, node.imap_name);
  const selected = selectedPath === folderPath || (node.special_use === 'inbox' && selectedPath === '/inbox');
  const isDropTarget = dropTargetId === node.id;

  return (
    <>
      <Tooltip title={`${node.imap_name} — drop mail here to move`} placement="right" enterDelay={600}>
        <ListItemButton
          selected={selected || isDropTarget}
          onClick={() =>
            onOpen(node.special_use === 'inbox' ? '/inbox' : folderPath, {
              toggleIfParent: hasChildren,
            })
          }
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropTargetId(node.id);
          }}
          onDragLeave={() => {
            if (dropTargetId === node.id) setDropTargetId(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDropTargetId(null);
            const target = readMessageDragTarget(e);
            if (target) onDropMessage(target, node);
          }}
          sx={{
            pl: 1.5 + depth * 1.75,
            outline: isDropTarget ? '2px solid' : undefined,
            outlineColor: isDropTarget ? 'primary.main' : undefined,
            bgcolor: isDropTarget ? 'action.selected' : undefined,
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            {hasChildren
              ? isOpen
                ? <FolderOpenIcon />
                : <FolderIcon />
              : SPECIAL_ICONS[node.special_use] || <FolderIcon />}
          </ListItemIcon>
          <ListItemText primary={node.name} primaryTypographyProps={{ noWrap: true }} />
          {node.unread_count > 0 && (
            <Badge badgeContent={node.unread_count} color="primary" max={999} sx={{ mr: hasChildren ? 0 : 1 }} />
          )}
          {hasChildren && (
            <IconButton
              size="small"
              edge="end"
              onClick={(e) => {
                e.stopPropagation();
                toggle(node.id);
              }}
              aria-label={isOpen ? 'Collapse folder' : 'Expand folder'}
            >
              {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
        </ListItemButton>
      </Tooltip>
      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {node.children.map((child) => (
              <FolderItem
                key={child.id}
                node={child}
                depth={depth + 1}
                selectedPath={selectedPath}
                onOpen={onOpen}
                expanded={expanded}
                toggle={toggle}
                dropTargetId={dropTargetId}
                setDropTargetId={setDropTargetId}
                onDropMessage={onDropMessage}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export default function Sidebar({ onNavigate }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [didInitExpand, setDidInitExpand] = useState(false);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['live-folders'],
    queryFn: () => getLiveFolders(),
  });

  const { data: accountsData } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: getMailAccounts,
  });

  const accounts = accountsData?.results || [];
  const folders = data?.results || [];

  const treesByAccount = useMemo(() => {
    const accountIds = [...new Set(folders.map((f) => f.account))];
    return accountIds.map((accountId) => ({
      accountId,
      name:
        accounts.find((a) => a.id === accountId)?.name ||
        accounts.find((a) => a.id === accountId)?.email_address ||
        `Account ${accountId}`,
      tree: buildTree(folders.filter((f) => f.account === accountId)),
    }));
  }, [folders, accounts]);

  useEffect(() => {
    if (!folders.length || didInitExpand) return;
    const ids = new Set<number>();
    treesByAccount.forEach(({ tree }) => collectExpandableIds(tree, ids));
    setExpanded(ids);
    setDidInitExpand(true);
  }, [folders, treesByAccount, didInitExpand]);

  useEffect(() => {
    // Expand parents of selected live folder
    const parts = location.pathname.split('/');
    if (parts[1] !== 'folder' || parts.length < 4) return;
    const accountId = parseInt(parts[2], 10);
    let imapName = '';
    try {
      imapName = decodeFolderKey(parts[3]);
    } catch {
      return;
    }
    const folder = folders.find((f) => f.account === accountId && f.imap_name === imapName);
    if (!folder) return;
    const byId = new Map(folders.map((f) => [f.id, f]));
    setExpanded((prev) => {
      const next = new Set(prev);
      let cur: Folder | undefined = folder;
      while (cur?.parent) {
        next.add(cur.parent);
        cur = byId.get(cur.parent);
      }
      return next;
    });
  }, [location.pathname, folders]);

  const go = (path: string, opts?: { toggleIfParent?: boolean }) => {
    navigate(path);
    onNavigate?.();
  };

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const dropMutation = useMutation({
    mutationFn: async ({
      target,
      folder,
    }: {
      target: NonNullable<ReturnType<typeof readMessageDragTarget>>;
      folder: Folder;
    }) => {
      if (isLiveTarget(target)) {
        await liveMoveMessage({
          account: target.account!,
          folder: target.imap_folder!,
          uid: target.uid!,
          dest_folder: folder.imap_name,
          dest_account: folder.account,
        });
        return;
      }
      if (target.id) {
        const { getMessage, getFolders } = await import('../api/mailbox');
        const detail = await getMessage(target.id);
        const dbFolders = await getFolders({ page_size: 5000 });
        const src = dbFolders.results.find((f) => f.id === detail.folder);
        if (!src) throw new Error('Source folder not found');
        await liveMoveMessage({
          account: src.account,
          folder: src.imap_name,
          uid: detail.uid,
          dest_folder: folder.imap_name,
          dest_account: folder.account,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['live-threads'] });
      qc.invalidateQueries({ queryKey: ['messages'] });
      qc.invalidateQueries({ queryKey: ['live-folders'] });
      qc.invalidateQueries({ queryKey: ['thread'] });
      qc.invalidateQueries({ queryKey: ['live-thread'] });
    },
  });

  return (
    <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {treesByAccount.map(({ accountId, name, tree }) => (
        <Box key={accountId}>
          {treesByAccount.length > 1 && (
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ px: 2, pt: 1, display: 'block' }}
            >
              {name}
            </Typography>
          )}
          <List dense>
            {tree.map((node) => (
              <FolderItem
                key={node.id}
                node={node}
                depth={0}
                selectedPath={location.pathname}
                onOpen={go}
                expanded={expanded}
                toggle={toggle}
                dropTargetId={dropTargetId}
                setDropTargetId={setDropTargetId}
                onDropMessage={(target, folder) => {
                  if (target) dropMutation.mutate({ target, folder });
                }}
              />
            ))}
          </List>
        </Box>
      ))}

      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List dense>
        <ListItemButton
          selected={location.pathname === '/contacts'}
          onClick={() => go('/contacts')}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ContactsIcon />
          </ListItemIcon>
          <ListItemText primary="Contacts" />
        </ListItemButton>
        <ListItemButton
          selected={location.pathname === '/settings'}
          onClick={() => go('/settings')}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItemButton>
      </List>
    </Box>
  );
}
